import IconAdd from "components/EvtKit/icons/IconAdd"
import IconDeleteRed from "components/EvtKit/icons/IconDeleteRed"
import Input from "components/EvtKit/input/Input"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import BodyText from "components/Typography/BodyText"
import { PricingUnits, unitSelectorOptions } from "pages/purchase-plans/constants/defaultValues"
import { ChangeElement, List } from "hooks/useHandleObjList"
import { formatPrice } from "utils/formatPrice"
import EventTypeSelector from "../pricing-variables/selectors/EventTypeSelector"
import InformationTooltip from "components/InformationTooltip"

import { memo, useCallback, useMemo, useState } from "react"
import styled from "styled-components"
import clsx from "clsx"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"

const InlineContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  align-items: flex-start;
`

const InputContainer = styled.div`
  width: 100%;
  diplay: flex;
  align-items: flex-start;
`

const PricingVariablesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PricingVariablesTitleContainer = styled.div`
  width: 100%;
  height: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PricingVariablesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const PricingVariablesItemContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  align-items: center;
  box-shadow: 0px 2px 6px 0px ${(props) => props.theme.palette.box_shadow};
  border-radius: 8px;
  padding: 8px;
`

const AddPricingVariablesButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &.disabled {
    cursor: default;
  }
`

const IconContainerDelete = styled.span`
  display: flex;
  justify-content: end;
  cursor: pointer;
`

const UnitContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const UnitTooltipContainer = styled.div`
  width: 100%;
  margin-left: 12px;
`

interface PricingVariablesListProps {
  pricingVariablesList: List<App.ISimplePricingVariable>
  addPricingVariable: (obj?: App.ISimplePricingVariable) => void
  removePricingVariable: (key: string) => void
  changePricingVariable: ChangeElement<App.ISimplePricingVariable>
  purchasePlanPricing: App.IPurchasePlanPricing
  handlePricingChange: (key: keyof App.IPurchasePlanPricing, value: string) => void
  selectedProduct: string
  isDevMode: boolean
  pricingUnit?: PricingUnits
  setPricingUnit: (value: PricingUnits) => void
  isUpdate?: boolean
}

const SimplePricingVariablesList = (props: PricingVariablesListProps) => {
  const {
    pricingVariablesList,
    addPricingVariable,
    removePricingVariable,
    changePricingVariable,
    purchasePlanPricing,
    handlePricingChange,
    selectedProduct,
    isDevMode,
    setPricingUnit,
    pricingUnit,
    isUpdate = false
  } = props

  const [prices, setPrices] = useState<Record<string, string>>({})

  const onPricingUnitChange = useCallback((option: Option | null) => {
    const selectorValue = option?.value ?? ""

    setPricingUnit(selectorValue as PricingUnits)
  }, [])

  const isAddNewPricingVariableBtnDisabled = useMemo(() => {
    const eventTypesSize = Object.values(prices).length

    const maxLimit = eventTypesSize === 0 ? 1 : eventTypesSize

    const maxLimitReached = pricingVariablesList.length >= maxLimit

    return maxLimitReached || isDevMode
  }, [pricingVariablesList.length, isDevMode, prices])

  const handleAddNewPricingVariable = useCallback(() => {
    if (isAddNewPricingVariableBtnDisabled) return

    addPricingVariable()
  }, [addPricingVariable, isAddNewPricingVariableBtnDisabled])

  const handleDelete = useCallback(
    (key: string) => () => {
      removePricingVariable(key)
    },
    [removePricingVariable]
  )

  const onSelectChange = useCallback(
    (key: string) => (option: Option | null) => {
      const selectorValue = option?.value ?? ""
      const selectorLabel = option?.label ?? ""

      changePricingVariable(key, "eventType", selectorValue)
      changePricingVariable(key, "label", selectorLabel)
    },
    [changePricingVariable]
  )

  const onLimitChange = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      changePricingVariable(key, "usageLimit", e.target.value)
    },
    [changePricingVariable]
  )

  const onInputChange = useCallback(
    (key: "fromPriceUnit" | "fromPrice") => (e: React.ChangeEvent<HTMLInputElement>) => {
      return handlePricingChange(key, e.target.value)
    },
    [changePricingVariable]
  )

  const onIsQuotaChange = useCallback((key, isQuota) => () => changePricingVariable(key, "isQuota", !isQuota), [])

  const hasSelectedProduct = selectedProduct.length > 0

  return (
    <PricingVariablesContainer>
      <PricingVariablesTitleContainer>
        {!hasSelectedProduct && (
          <BodyText size="small" weight="semi-bold" color="gray-medium">
            Definições para precificação (selecione um produto para continuar):
          </BodyText>
        )}
        {!!hasSelectedProduct && (
          <BodyText size="small" weight="semi-bold" color="gray-dark">
            Definições para precificação:
          </BodyText>
        )}

        {!isDevMode && (
          <AddPricingVariablesButton
            type="button"
            onClick={handleAddNewPricingVariable}
            className={clsx({ disabled: isAddNewPricingVariableBtnDisabled })}
          >
            <IconAdd disabled={isAddNewPricingVariableBtnDisabled} />
          </AddPricingVariablesButton>
        )}
      </PricingVariablesTitleContainer>

      {hasSelectedProduct && (
        <PricingVariablesListContainer>
          <InlineContainer>
            <UnitContainer>
              {!isDevMode && (
                <SelectV2
                  className="fullWidth"
                  id="unit"
                  label="Cobrança por *"
                  options={unitSelectorOptions}
                  placeholder="Selecione"
                  shrink
                  value={pricingUnit}
                  onChange={onPricingUnitChange}
                  disabled={isUpdate}
                />
              )}

              {isDevMode && (
                <InputContainer>
                  <Input
                    className="fullWidth"
                    label="Cobrança por *"
                    name="fromPriceUnit"
                    onChange={onInputChange("fromPriceUnit")}
                    placeholder="Digite"
                    shrink
                    value={purchasePlanPricing.fromPriceUnit || ""}
                  />
                </InputContainer>
              )}
              <UnitTooltipContainer>
                <InformationTooltip text="é utilizado para mostrar nas telas de exibição de planos e nas 'invoices' de relatório de uso" />
              </UnitTooltipContainer>
            </UnitContainer>

            {pricingUnit === PricingUnits.MONTHLY && !isDevMode && (
              <InputContainer>
                <Input
                  id="fromPrice"
                  className="fullWidth"
                  label="Valor Mensal *"
                  name="fromPrice"
                  onChange={onInputChange("fromPrice")}
                  placeholder="Digite"
                  shrink
                  value={purchasePlanPricing.fromPrice || ""}
                  type="number"
                />
              </InputContainer>
            )}
          </InlineContainer>

          {!isDevMode &&
            !!pricingUnit &&
            pricingVariablesList.map((pricingVariable) => {
              return (
                <PricingVariablesItemContainer key={pricingVariable.key}>
                  <EventTypeSelector
                    onSelectorChange={onSelectChange(pricingVariable.key)}
                    value={pricingVariable.eventType}
                    setPrices={setPrices}
                    disabled={!pricingVariable.isUpdatable}
                    pricingVariablesList={pricingVariablesList}
                  />

                  {!!prices[pricingVariable.eventType] && pricingUnit === PricingUnits.CONSUMPTION && (
                    <InputContainer>
                      <Input
                        id={`input-${pricingVariable.key}`}
                        className="fullWidth"
                        label="Preço por evento"
                        name="eventPrice"
                        shrink
                        value={formatPrice(+prices[pricingVariable.eventType], "BRL").toString() ?? ""}
                        readOnly
                      />
                    </InputContainer>
                  )}

                  {pricingUnit === PricingUnits.MONTHLY && (
                    <>
                      <InputContainer>
                        <Input
                          id={`usageLimit-input-${pricingVariable.key}`}
                          className="fullWidth"
                          label="Limite *"
                          placeholder="Digite"
                          name="usageLimit"
                          shrink
                          value={pricingVariable.usageLimit}
                          onChange={onLimitChange(pricingVariable.key)}
                          type="number"
                          disabled={!pricingVariable.isUpdatable}
                        />
                      </InputContainer>

                      <InputContainer>
                        <SwitchV2
                          label="É uma variável de quota?"
                          value={pricingVariable.isQuota}
                          onChange={onIsQuotaChange(pricingVariable.key, pricingVariable.isQuota)}
                          variant="medium"
                          grayBackground
                          disabled={!pricingVariable.isUpdatable}
                        />
                      </InputContainer>
                    </>
                  )}

                  {pricingVariablesList.length > 1 && pricingVariable.isUpdatable && (
                    <IconContainerDelete onClick={handleDelete(pricingVariable.key)}>
                      <IconDeleteRed />
                    </IconContainerDelete>
                  )}
                </PricingVariablesItemContainer>
              )
            })}
        </PricingVariablesListContainer>
      )}
    </PricingVariablesContainer>
  )
}

export default memo(SimplePricingVariablesList)
